import { computed, type Ref } from 'vue'

import type {
  SeasonalityKeyword,
  TrendDecompositionTab,
  TrendKeyword,
} from '../models/trendSeasonality.types'
import { buildRollingPivot, buildSeasonalPivot, type TrendPivotRow } from '../service/trendPivot'

import { useI18n } from '@/shared/i18n'
import type { TableColumn } from '@/shared/ui'

const MONTH_I18N_KEYS = [
  'january',
  'february',
  'march',
  'april',
  'may',
  'june',
  'july',
  'august',
  'september',
  'october',
  'november',
  'december',
] as const

export type TrendPivotKeyword = TrendKeyword | SeasonalityKeyword

export function useTrendPivot(
  keywords: Ref<readonly TrendPivotKeyword[]>,
  decomposition: Ref<TrendDecompositionTab>,
) {
  const { t } = useI18n()
  const pivot = computed(() =>
    decomposition.value === 'seasonal'
      ? buildSeasonalPivot(keywords.value as readonly SeasonalityKeyword[])
      : buildRollingPivot(
          keywords.value as readonly TrendKeyword[],
          decomposition.value === 'volume'
            ? 'observed'
            : decomposition.value === 'random'
              ? 'residual'
              : 'trend',
        ),
  )
  const monthColumns = computed(() =>
    pivot.value.columns.map((column) => ({
      ...column,
      label: labelFor(column.month, column.year),
    })),
  )
  const columns = computed<TableColumn<TrendPivotRow>[]>(() => [
    {
      field: 'query',
      header: t('marketingInsights.trendSeasonality.pivot.query'),
      width: 224,
      frozen: true,
      sortable: true,
    },
    ...monthColumns.value.map((column): TableColumn<TrendPivotRow> => ({
      field: column.field,
      header: column.label,
      width: 112,
      numeric: true,
      align: 'end',
      sortable: true,
    })),
  ])

  function labelFor(month: number, year: number | null): string {
    const monthKey = MONTH_I18N_KEYS[month - 1]
    const name = monthKey
      ? t(`marketingInsights.trendSeasonality.months.${monthKey}`)
      : String(month)
    return year === null ? name : `${name} ${String(year).slice(-2)}`
  }

  return {
    columns,
    monthColumns,
    rows: computed(() => pivot.value.rows),
    total: computed(() => pivot.value.total),
  }
}
